import { Box, Tooltip, Typography } from "@mui/material";
import type React from "react";

import { type FunnelStep, type SourceMeta, TERM_FOR_KEY } from "../helpers/outreachSources";
import { Spacing } from "../types/enum";

/**
 * One tool's funnel: how far the members it reached actually got.
 *
 * Bars rather than tiles, sharing one scale anchored to `reached`, because the
 * question is always "of the people we reached, how many…" — and a row of tiles
 * makes 38 and 6 look like two facts rather than one fraction. Every bar is the
 * same hue: this is magnitude, not identity, and colouring each step differently
 * would imply the steps are categories to be told apart rather than a sequence
 * to be read down.
 *
 * The delta against the period before is the point of the whole screen. A number
 * on its own says almost nothing — "12 saved" is good or terrible depending on
 * last month — so it always travels with the comparison.
 */

const BAR_COLOR = "#00583D";
const TRACK_COLOR = "rgba(0,0,0,0.04)";
const GOOD = "#1B7F4C";
const BAD = "#B3261E";

export interface Funnel {
  reached: number;
  sends: number;
  responded: number;
  accepted: number;
  declined: number;
  attendedInvited: number;
  attendedAny: number;
  stillZero: number;
  loggedIn: number;
  stayed: number;
  leaving: number;
  cancelled: number;
  paused: number;
  medianAttended?: number | null;
  eventsAttended: number;
}

/**
 * A step where a *smaller* share is the better outcome, so the delta arrow
 * doesn't congratulate us for a rise in members who left.
 */
const LOWER_IS_BETTER = new Set(["cancelled", "declined", "stillZero", "leaving"]);

const share = (value: number, total: number): number | null =>
  total > 0 ? Math.round((value / total) * 100) : null;

/**
 * The change in a step's *share*, not its count.
 *
 * Reaching twice as many people doubles every count without anything having
 * improved, so comparing counts would call a bigger send a better one. The rate
 * is what says whether the tool got better at its job.
 */
const Delta: React.FC<{
  step: FunnelStep;
  current: Funnel;
  previous?: Funnel | null;
}> = ({ step, current, previous }) => {
  if (!previous || previous.reached === 0 || current.reached === 0) {
    return null;
  }
  const now = share(current[step.key] as number, current.reached);
  const before = share(previous[step.key] as number, previous.reached);
  if (now === null || before === null || now === before) {
    return null;
  }
  const points = now - before;
  const better = LOWER_IS_BETTER.has(step.key) ? points < 0 : points > 0;
  return (
    <Tooltip
      title={`${before}% over the period immediately before this one, ${now}% now — measured the same way, so the change is real.`}
    >
      <Typography
        variant="caption"
        sx={{ color: better ? GOOD : BAD, fontWeight: 600, whiteSpace: "nowrap" }}
      >
        {points > 0 ? "▲" : "▼"} {Math.abs(points)} pts
      </Typography>
    </Tooltip>
  );
};

const OutreachFunnel: React.FC<{
  meta: SourceMeta;
  current: Funnel;
  previous?: Funnel | null;
  /**
   * A question mark for one step. The label's tooltip says what the step means
   * *for this tool*; this says what the measure means in general, which is the
   * part that decides whether a percentage can be trusted.
   */
  helpButton?: (term?: string) => React.ReactNode;
}> = ({ meta, current, previous, helpButton }) => {
  const total = current.reached;

  if (total === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        Nothing sent from {meta.label} in this period.
      </Typography>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={Spacing.s}>
      {meta.steps.map((step) => {
        const value = current[step.key] as number;
        const percent = share(value, total);
        const width = total > 0 ? Math.max((value / total) * 100, value > 0 ? 1.5 : 0) : 0;
        return (
          <Box
            key={step.key}
            display="grid"
            alignItems="center"
            gap={Spacing.s}
            sx={{ gridTemplateColumns: { xs: "1fr", sm: "190px 1fr 132px" } }}
          >
            <Box display="flex" alignItems="center" minWidth={0}>
              <Tooltip title={step.hint}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {step.label}
                </Typography>
              </Tooltip>
              {helpButton?.(TERM_FOR_KEY[step.key])}
            </Box>

            <Box sx={{ height: 14, bgcolor: TRACK_COLOR, borderRadius: 1, overflow: "hidden" }}>
              <Box
                sx={{ width: `${width}%`, height: "100%", bgcolor: BAR_COLOR, borderRadius: 1 }}
              />
            </Box>

            <Box display="flex" alignItems="baseline" gap={Spacing.xs}>
              <Typography variant="body2" fontWeight={700}>
                {value}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {step.key === "reached"
                  ? `· ${current.sends} sends`
                  : percent !== null && `· ${percent}%`}
              </Typography>
              <Delta step={step} current={current} previous={previous} />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default OutreachFunnel;
